import React from 'react';
import {
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ScaleIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { pctSymbol } from '../../utils/formatters';
import { useCurrency } from '../../hooks/useCurrency';

const ValuationTile = ({ valuation, user }) => {
  // Add debugging to trace the user data
  console.log('ValuationTile user:', user);

  const { format: _currencyFormat, symbol: _currencySymbol } = useCurrency();

  const formattedValuation = _currencyFormat(Number(valuation || 0), { maximumFractionDigits: 0 });

  // Format currency values
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return `${_currencySymbol}0`;
    return _currencyFormat(Number(value), { maximumFractionDigits: 0 });
  };
  
  // Format percentage values
  const formatPercent = (value) => {
    if (value === undefined || value === null) return `0${pctSymbol()}`;
    return `${parseFloat(value).toFixed(1)}${pctSymbol()}`;
  };
  
  // Format number values
  const formatNumber = (value) => {
    if (value === undefined || value === null) return '0';
    return new Intl.NumberFormat('en-US').format(value);
  };

  // User metrics with succession theme colors
  const userMetrics = [
    {
      name: 'Gross Production',
      value: formatCurrency(user?.gross_production),
      description: 'Annual production',
      icon: CurrencyDollarIcon,
      color: 'text-succession-success',
      bgColor: 'bg-succession-success-bg'
    },
    {
      name: 'Recurring Revenue',
      value: formatCurrency(user?.recurring_revenue),
      description: 'Recurring revenue portion of gross production',
      icon: BanknotesIcon,
      color: 'text-succession-primary',
      bgColor: 'bg-succession-primary-10'
    },
    {
      name: 'Total Assets',
      value: formatCurrency(user?.assets_under_management),
      description: 'Assets under management',
      icon: ScaleIcon,
      color: 'text-succession-secondary',
      bgColor: 'bg-succession-secondary-10'
    },
    {
      name: 'Total Clients',
      value: formatNumber(user?.number_of_clients),
      description: 'Active clients',
      icon: UserGroupIcon,
      color: 'text-succession-accent',
      bgColor: 'bg-succession-accent-10'
    }
  ];

  return (
    <div className="bg-succession-bg-primary shadow-succession-md rounded-lg p-6 border border-succession-border transition-colors duration-300">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-succession-text-primary transition-colors duration-300">Practice Valuation</h3>
        <ArrowTrendingUpIcon className="h-6 w-6 text-succession-success transition-colors duration-300" />
      </div>
      <div className="mt-4">
        <p className="text-4xl font-semibold text-succession-text-primary transition-colors duration-300">{formattedValuation}</p>
        <p className="mt-1 text-sm text-succession-text-secondary transition-colors duration-300">Estimated practice value</p>
      </div>
      <div className="mt-4">
        <div className="flex items-center">
          <span className="text-succession-success text-sm font-medium transition-colors duration-300">↑ 12{pctSymbol()}</span>
          <span className="ml-2 text-sm text-succession-text-secondary transition-colors duration-300">from last year</span>
        </div>
      </div>

      {/* User metrics cards */}
      <div className="mt-8 grid grid-cols-2 gap-4">
        {userMetrics.map((metric) => (
          <div key={metric.name} className="bg-succession-bg-primary overflow-hidden rounded-lg border border-succession-border h-full hover:shadow-succession-md hover:border-succession-border-hover transition-all duration-300">
            <div className="px-4 py-5 sm:p-4">
              <div className="flex items-center">
                <div className={`flex-shrink-0 rounded-md p-2 ${metric.bgColor} transition-colors duration-300`}>
                  <metric.icon className={`h-5 w-5 ${metric.color} transition-colors duration-300`} aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <p className="text-xs font-medium text-succession-text-secondary transition-colors duration-300">{metric.name}</p>
                  <p className="text-base font-semibold text-succession-text-primary transition-colors duration-300">{metric.value}</p>
                  <p className="text-xs text-succession-text-muted transition-colors duration-300">{metric.description}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <a
          href="/succession/valuation"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-succession-primary hover:bg-succession-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary transition-all duration-300"
        >
          View Details
        </a>
      </div>
    </div>
  );
};

export default ValuationTile; 